/**
 * Dashboard Redirect Component
 * 
 * Redireciona usuários para o dashboard apropriado baseado no tipo de organização:
 * - Super Admin -> /super-admin (Dashboard Global)
 * - organization_type: "shopping" -> Dashboard de Pessoas (CompleteDashboard)
 * - organization_type: "vehicle_counting" -> Dashboard de Veículos (VehicleDashboardPage)
 * - organization_type: "parking" -> Gestão de Estacionamento (/parking)
 * - organization_type: "suspect_alerts_only" -> Alertas de Suspeitos (/suspect-alerts)
 * - organization_type: "heatmap" -> Mapa de Calor por Zonas (/heatmap)
 */

import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from './DashboardLayout';
import { CompleteDashboard } from '../presentation/components/CompleteDashboard';
import SuperAdminDashboardPage from '../pages/SuperAdminDashboardPage';
import VehicleDashboardPage from '../pages/VehicleDashboardPage';
import PaymentWarningModal from './PaymentWarningModal';
import { environment } from '../config/environment';

export function DashboardRedirect() {
  const { user, activeOrganizationId } = useAuth();
  const [organizationType, setOrganizationType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Usar activeOrganizationId (que funciona para group users e non-group users)
  const effectiveOrgId = activeOrganizationId || user?.organization_id;

  useEffect(() => {
    async function fetchOrganizationType() {
      if (!effectiveOrgId || user?.is_superuser) {
        setLoading(false);
        return;
      }

      try {
        const activeOrgForHeader = localStorage.getItem('awflux_active_organization_id');
        const headers: Record<string, string> = {
              'Authorization': `Bearer ${localStorage.getItem('awflux_token')}`,
            };
        if (activeOrgForHeader) {
              headers['X-Organization-Id'] = activeOrgForHeader;
            }
        const response = await fetch(
          `${environment.apiBaseUrl}/api/v1/organizations/${effectiveOrgId}`,
          {
            headers,
          }
        );

        if (response.ok) {
          const data = await response.json();
          setOrganizationType(data.organization_type || 'shopping');
        } else {
          console.error('Erro ao buscar tipo de organização');
          setOrganizationType('shopping'); // Fallback para shopping
        }
      } catch (error) {
        console.error('Erro ao buscar tipo de organização:', error);
        setOrganizationType('shopping'); // Fallback para shopping
      } finally {
        setLoading(false);
      }
    }

    fetchOrganizationType();
  }, [effectiveOrgId, user?.is_superuser]);

  // Super Admin vai para dashboard global
  if (user?.role === 'partner') {
    return <Navigate to="/partner" replace />;
  }
  if (user?.is_superuser) {
    return (
      <DashboardLayout>
        <SuperAdminDashboardPage />
      </DashboardLayout>
    );
  }

  // Aguardar carregamento do tipo de organização
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Carregando dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // 💳 Modal de Aviso de Pagamento (não-superusers)
  const dashboardWithPaymentCheck = (children: React.ReactNode) => (
    <DashboardLayout>
      <PaymentWarningModal />
      {children}
    </DashboardLayout>
  );

  // 🚗 Organização de Veículos -> Dashboard de Veículos
  if (organizationType === 'vehicle_counting') {
    return dashboardWithPaymentCheck(<VehicleDashboardPage />);
  }

  // 🅿️ Organização de Estacionamento -> Página de Gestão de Estacionamento
  // Nota: PaymentWarningModal é renderizado dentro de ParkingManagementPage
  // pois <Navigate> desmonta este componente antes do modal completar o fetch
  if (organizationType === 'parking') {
    return (
      <DashboardLayout>
        <Navigate to="/parking" replace />
      </DashboardLayout>
    );
  }

  // 🚨 Suspect Alerts Only -> redireciona direto para Alertas de Suspeitos
  if (organizationType === 'suspect_alerts_only') {
    return (
      <DashboardLayout>
        <Navigate to="/suspect-alerts" replace />
      </DashboardLayout>
    );
  }

  // 🔥 Organização de Mapa de Calor -> Zonas de Calor (dashboard exclusivo)
  // Câmeras de zona (is_entrance=false) só aparecem no mapa de calor;
  // o dashboard de contagem de pessoas ficaria vazio/irrelevante.
  if (organizationType === 'heatmap') {
    return (
      <DashboardLayout>
        <Navigate to="/heatmap" replace />
      </DashboardLayout>
    );
  }

  // 🏬 Organização de Shopping -> Dashboard de Pessoas (padrão)
  return dashboardWithPaymentCheck(<CompleteDashboard />);
}
